import { Question, Section } from '../types';

export const questionnaire: { sections: Section[] } = {
  sections: [
    {
      id: 'company-profile',
      title: 'Company Profile',
      questions: [
        {
          id: 'companyName',
          label: 'Company name',
          type: 'text',
          required: true,
          placeholder: 'Enter your company name'
        },
        {
          id: 'businessEmail',
          label: 'Business email address',
          type: 'text',
          required: true,
          placeholder: 'name@company.com',
          validation: 'email'
        },
        {
          id: 'industry',
          label: 'Industry',
          type: 'text',
          required: true
        },
        {
          id: 'annualRevenue',
          label: 'Approximate annual revenue',
          type: 'select',
          required: true,
          options: [
            { value: 'below-10m', label: 'Below EUR 10 million' },
            { value: '10-50m', label: 'EUR 10–50 million' },
            { value: '50-100m', label: 'EUR 50–100 million' },
            { value: '100-250m', label: 'EUR 100–250 million' },
            { value: 'above-250m', label: 'Above EUR 250 million' }
          ]
        },
        {
          id: 'employeeCount',
          label: 'Number of employees worldwide',
          type: 'select',
          required: true,
          options: [
            { value: 'below-50', label: 'Below 50' },
            { value: '50-249', label: '50–249' },
            { value: '250-499', label: '250–499' },
            { value: '500-999', label: '500–999' },
            { value: '1000-plus', label: '1,000 or more' }
          ]
        },
        {
          id: 'mainProductsServices',
          label: 'Main products / services',
          type: 'textarea',
          required: true,
          placeholder: 'Please describe your main products or services.'
        },
        {
          id: 'mainCompetitors',
          label: 'Main competitors',
          type: 'textarea',
          required: true,
          placeholder: 'Please describe your main competitors.'
        }
      ]
    },
    {
      id: 'current-china-activities',
      title: 'Current China Activities',
      questions: [
        {
          id: 'sellsInChina',
          label: 'Are your company\'s own products currently sold in China?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'sellsInChinaComment',
          label: 'Optional comment',
          type: 'textarea',
          required: false,
          placeholder: 'Please briefly describe your current sales activities in China.'
        },
        {
          id: 'sourcesFromChina',
          label: 'Does your company currently source products, components or services from China?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'sourcesFromChinaComment',
          label: 'Optional comment',
          type: 'textarea',
          required: false,
          placeholder: 'Please briefly describe your current sourcing activities from China.'
        },
        {
          id: 'hasLegalEntity',
          label: 'Does your company currently have its own legal entity in China?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'hasLegalEntityComment',
          label: 'Optional comment',
          type: 'textarea',
          required: false,
          placeholder: 'Please briefly describe your current legal presence in China.'
        },
        {
          id: 'hasOfficeOrEmployees',
          label: 'Does your company currently employ personnel or operate an office in China?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'hasOfficeOrEmployeesComment',
          label: 'Optional comment',
          type: 'textarea',
          required: false,
          placeholder: 'Please briefly describe your current office or employee presence in China.'
        },
        {
          id: 'howSellInChina',
          label: 'How do you currently sell in China?',
          type: 'checkbox',
          required: false,
          options: [
            { value: 'own-sales-team', label: 'Own sales team' },
            { value: 'distributor', label: 'Distributor' },
            { value: 'sales-agent', label: 'Sales agent' },
            { value: 'headquarters-direct', label: 'Directly from headquarters' },
            { value: 'oem-partner', label: 'OEM / partner' },
            { value: 'e-commerce', label: 'E-commerce' },
            { value: 'other', label: 'Other' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'sellsInChina',
                operator: 'equals',
                value: 'yes'
              }
            ]
          }
        },
        {
          id: 'chinaFunctions',
          label: 'Which functions do you currently perform in China?',
          type: 'checkbox',
          required: false,
          options: [
            { value: 'sales', label: 'Sales' },
            { value: 'service', label: 'Service' },
            { value: 'sourcing', label: 'Sourcing' },
            { value: 'assembly', label: 'Assembly' },
            { value: 'production', label: 'Production' },
            { value: 'warehouse-logistics', label: 'Warehouse / logistics' },
            { value: 'rd-engineering', label: 'R&D / engineering' },
            { value: 'administration', label: 'Administration' },
            { value: 'other', label: 'Other' }
          ],
          visibleWhen: {
            any: [
              {
                questionId: 'hasLegalEntity',
                operator: 'equals',
                value: 'yes'
              },
              {
                questionId: 'hasOfficeOrEmployees',
                operator: 'equals',
                value: 'yes'
              }
            ]
          }
        },
        {
          id: 'chinaEmployeesCount',
          label: 'Approximate number of employees in China',
          type: 'number',
          required: false,
          placeholder: 'Enter number',
          visibleWhen: {
            any: [
              {
                questionId: 'hasLegalEntity',
                operator: 'equals',
                value: 'yes'
              },
              {
                questionId: 'hasOfficeOrEmployees',
                operator: 'equals',
                value: 'yes'
              }
            ]
          }
        }
      ]
    },
    {
      id: 'china-plans',
      title: 'China Plans',
      questions: [
        {
          id: 'plannedActivities',
          label: 'Which activities are you considering or planning in China?',
          type: 'checkbox',
          required: true,
          options: [
            { value: 'assembly', label: 'Assembly' },
            { value: 'production', label: 'Production' },
            { value: 'service', label: 'Service' },
            { value: 'job-shop', label: 'Job shop' },
            { value: 'sales', label: 'Sales' },
            { value: 'sourcing', label: 'Sourcing' },
            { value: 'warehouse-logistics', label: 'Warehouse / logistics' },
            { value: 'rd-engineering', label: 'R&D / engineering' },
            { value: 'other', label: 'Other' }
          ]
        },
        {
          id: 'chinaProjectStatus',
          label: 'What is the current status of your China project?',
          type: 'select',
          required: true,
          options: [
            { value: 'implementing', label: 'Already being implemented' },
            { value: 'within-12-months', label: 'Concrete project within the next 12 months' },
            { value: '12-24-months', label: 'Concrete project within 12–24 months' },
            { value: 'longer-term', label: 'Longer-term plan' },
            { value: 'exploring', label: 'Currently exploring options' },
            { value: 'no-project', label: 'No specific project at this time' }
          ]
        }
      ]
    },
    {
      id: 'motivation',
      title: 'Motivation',
      questions: [
        {
          id: 'motivationGainingLocalBusiness',
          label: 'Gaining local Chinese business',
          type: 'select',
          required: true,
          options: [
            { value: 'high-priority', label: 'High priority' },
            { value: 'relevant', label: 'Relevant' },
            { value: 'not-relevant', label: 'Not relevant' }
          ]
        },
        {
          id: 'motivationFollowingCustomers',
          label: 'Following existing customers',
          type: 'select',
          required: true,
          options: [
            { value: 'high-priority', label: 'High priority' },
            { value: 'relevant', label: 'Relevant' },
            { value: 'not-relevant', label: 'Not relevant' }
          ]
        },
        {
          id: 'motivationCostReduction',
          label: 'Reduction of costs',
          type: 'select',
          required: true,
          options: [
            { value: 'high-priority', label: 'High priority' },
            { value: 'relevant', label: 'Relevant' },
            { value: 'not-relevant', label: 'Not relevant' }
          ]
        },
        {
          id: 'motivationLocalContent',
          label: 'Delivering local content',
          type: 'select',
          required: true,
          options: [
            { value: 'high-priority', label: 'High priority' },
            { value: 'relevant', label: 'Relevant' },
            { value: 'not-relevant', label: 'Not relevant' }
          ]
        },
        {
          id: 'motivationDeliveryTimelines',
          label: 'Shortening delivery / service timelines',
          type: 'select',
          required: true,
          options: [
            { value: 'high-priority', label: 'High priority' },
            { value: 'relevant', label: 'Relevant' },
            { value: 'not-relevant', label: 'Not relevant' }
          ]
        },
        {
          id: 'motivationLocalCurrency',
          label: 'Invoicing in local currency',
          type: 'select',
          required: true,
          options: [
            { value: 'high-priority', label: 'High priority' },
            { value: 'relevant', label: 'Relevant' },
            { value: 'not-relevant', label: 'Not relevant' }
          ]
        },
        {
          id: 'motivationLogisticsCosts',
          label: 'Reduction of logistics costs',
          type: 'select',
          required: true,
          options: [
            { value: 'high-priority', label: 'High priority' },
            { value: 'relevant', label: 'Relevant' },
            { value: 'not-relevant', label: 'Not relevant' }
          ]
        },
        {
          id: 'motivationOther',
          label: 'Other motivation',
          type: 'textarea',
          required: false,
          placeholder: 'Please describe any other motivations.'
        }
      ]
    },
    {
      id: 'concrete-setup',
      title: 'Concrete Setup',
      questions: [
        {
          id: 'certificationsRequired',
          label: 'Are specific certifications required for your China operation?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'not-sure', label: 'Not sure' }
          ],
          visibleWhen: {
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        },
        {
          id: 'certificationsDetails',
          label: 'Which certifications or standards are relevant?',
          type: 'textarea',
          required: false,
          placeholder: 'Please list the relevant certifications or standards.',
          visibleWhen: {
            all: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'includes',
                value: ['implementing', 'within-12-months', '12-24-months']
              },
              {
                questionId: 'certificationsRequired',
                operator: 'includes',
                value: ['yes', 'not-sure']
              }
            ]
          }
        },
        {
          id: 'shiftWorkPlanned',
          label: 'Is shift work planned?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'not-sure', label: 'Not sure' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'includes',
                value: ['implementing', 'within-12-months', '12-24-months']
              }
            ]
          }
        },
        {
          id: 'shiftCount',
          label: 'How many shifts are planned?',
          type: 'select',
          required: false,
          options: [
            { value: '1', label: '1 shift' },
            { value: '2', label: '2 shifts' },
            { value: '3', label: '3 shifts' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'includes',
                value: ['implementing', 'within-12-months', '12-24-months']
              },
              {
                questionId: 'shiftWorkPlanned',
                operator: 'equals',
                value: 'yes'
              }
            ]
          }
        }
      ]
    },
    {
      id: 'facility-requirement',
      title: 'Facility Requirement',
      questions: [
        {
          id: 'physicalSpaceRequirement',
          label: 'Do you currently have a concrete requirement for physical space in China?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'probably', label: 'Probably, but requirements are not yet final' },
            { value: 'no', label: 'No' }
          ],
          visibleWhen: {
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        },
        {
          id: 'requiredSpaceTypes',
          label: 'Which types of space do you expect to require?',
          type: 'checkbox',
          required: false,
          options: [
            { value: 'office', label: 'Office' },
            { value: 'workshop', label: 'Workshop / assembly / production' },
            { value: 'warehouse', label: 'Warehouse' },
            { value: 'laboratory', label: 'Laboratory / testing' },
            { value: 'other', label: 'Other' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'physicalSpaceRequirement',
                operator: 'equals',
                value: 'yes'
              }
            ],
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        },
        {
          id: 'requiredOfficeArea',
          label: 'Required office net area [sqm]',
          type: 'number',
          required: false,
          placeholder: 'Enter square meters',
          visibleWhen: {
            all: [
              {
                questionId: 'physicalSpaceRequirement',
                operator: 'equals',
                value: 'yes'
              },
              {
                questionId: 'requiredSpaceTypes',
                operator: 'includes',
                value: 'office'
              }
            ],
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        },
        {
          id: 'requiredWorkshopArea',
          label: 'Required workshop / production net area [sqm]',
          type: 'number',
          required: false,
          placeholder: 'Enter square meters',
          visibleWhen: {
            all: [
              {
                questionId: 'physicalSpaceRequirement',
                operator: 'equals',
                value: 'yes'
              },
              {
                questionId: 'requiredSpaceTypes',
                operator: 'includes',
                value: 'workshop'
              }
            ],
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        },
        {
          id: 'requiredWarehouseArea',
          label: 'Required warehouse area [sqm]',
          type: 'number',
          required: false,
          placeholder: 'Enter square meters',
          visibleWhen: {
            all: [
              {
                questionId: 'physicalSpaceRequirement',
                operator: 'equals',
                value: 'yes'
              },
              {
                questionId: 'requiredSpaceTypes',
                operator: 'includes',
                value: 'warehouse'
              }
            ],
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        },
        {
          id: 'requiredLaboratoryArea',
          label: 'Required laboratory / testing area [sqm]',
          type: 'number',
          required: false,
          placeholder: 'Enter square meters',
          visibleWhen: {
            all: [
              {
                questionId: 'physicalSpaceRequirement',
                operator: 'equals',
                value: 'yes'
              },
              {
                questionId: 'requiredSpaceTypes',
                operator: 'includes',
                value: 'laboratory'
              }
            ],
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        },
        {
          id: 'specialRequirements',
          label: 'Does your operation have any special technical, infrastructure, safety or regulatory requirements?',
          type: 'radio',
          required: true,
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'not-sure', label: 'Not sure yet' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'physicalSpaceRequirement',
                operator: 'equals',
                value: 'yes'
              }
            ],
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        },
        {
          id: 'specialRequirementsDetails',
          label: 'Please describe the special requirements',
          type: 'textarea',
          required: false,
          placeholder: 'Examples include cleanroom requirements, medical or pharmaceutical standards, chemical handling or storage, hazardous materials, special ventilation, compressed air, high-pressure pneumatic supply, special electrical power, ESD protection, temperature or humidity control, crane capacity, special foundations or other infrastructure requirements.',
          visibleWhen: {
            all: [
              {
                questionId: 'physicalSpaceRequirement',
                operator: 'equals',
                value: 'yes'
              },
              {
                questionId: 'specialRequirements',
                operator: 'includes',
                value: ['yes', 'not-sure']
              }
            ],
            any: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'implementing'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: 'within-12-months'
              },
              {
                questionId: 'chinaProjectStatus',
                operator: 'equals',
                value: '12-24-months'
              }
            ]
          }
        }
      ]
    },
    {
      id: 'business-planning',
      title: 'Business Planning',
      questions: [
        {
          id: 'businessPlanningProducts',
          label: 'Products / Services',
          type: 'repeatable-table',
          required: false,
          columns: [
            { id: 'productService', label: 'Product / Service', type: 'text' },
            { id: 'year1Sales', label: 'Year 1 net sales [EUR/month]', type: 'number' },
            { id: 'year2Sales', label: 'Year 2 net sales [EUR/month]', type: 'number' },
            { id: 'year3Sales', label: 'Year 3 net sales [EUR/month]', type: 'number' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'includes',
                value: ['implementing', 'within-12-months', '12-24-months']
              }
            ]
          }
        },
        {
          id: 'tradingSales',
          label: 'Estimated trading sales',
          type: 'repeatable-table',
          required: false,
          columns: [
            { id: 'year', label: 'Year', type: 'text' },
            { id: 'sales', label: 'Net sales [EUR/month]', type: 'number' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'includes',
                value: ['implementing', 'within-12-months', '12-24-months']
              }
            ]
          }
        },
        {
          id: 'materialRate',
          label: 'Average material rate [%]',
          type: 'number',
          required: false,
          placeholder: 'Enter percentage',
          visibleWhen: {
            all: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'includes',
                value: ['implementing', 'within-12-months', '12-24-months']
              }
            ]
          }
        }
      ]
    },
    {
      id: 'staff-planning',
      title: 'Staff Planning',
      questions: [
        {
          id: 'staffPlanning',
          label: 'Staff planning',
          type: 'repeatable-table',
          required: false,
          columns: [
            { id: 'jobTitle', label: 'Job title', type: 'text' },
            { id: 'year1Employees', label: 'Year 1 employees', type: 'number' },
            { id: 'year2Employees', label: 'Year 2 employees', type: 'number' },
            { id: 'year3Employees', label: 'Year 3 employees', type: 'number' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'includes',
                value: ['implementing', 'within-12-months', '12-24-months']
              }
            ]
          }
        }
      ]
    },
    {
      id: 'investment-planning',
      title: 'Investment Planning',
      questions: [
        {
          id: 'investmentPlanning',
          label: 'Investment planning',
          type: 'repeatable-table',
          required: false,
          columns: [
            { id: 'equipmentName', label: 'Equipment name', type: 'text' },
            { id: 'netValue', label: 'Net value [EUR]', type: 'number' },
            { id: 'quantityYear1', label: 'Quantity Year 1', type: 'number' },
            { id: 'quantityYear2', label: 'Quantity Year 2', type: 'number' },
            { id: 'quantityYear3', label: 'Quantity Year 3', type: 'number' }
          ],
          visibleWhen: {
            all: [
              {
                questionId: 'chinaProjectStatus',
                operator: 'includes',
                value: ['implementing', 'within-12-months', '12-24-months']
              }
            ]
          }
        }
      ]
    },
    {
      id: 'challenges-support',
      title: 'Challenges & Support',
      questions: [
        {
          id: 'drivingInterest',
          label: 'What is currently driving your interest in China?',
          type: 'textarea',
          required: true,
          placeholder: 'Please briefly describe the main business reasons, opportunities or developments behind your current interest in China.'
        },
        {
          id: 'currentChallenges',
          label: 'Where do you currently see the biggest challenges or uncertainties regarding China?',
          type: 'textarea',
          required: true,
          placeholder: 'For example: market access, sales, sourcing, regulations, localization, recruitment, production, partner selection or internal resources.'
        },
        {
          id: 'supportPriorities',
          label: 'Are there any specific topics where you would currently like support or further information?',
          type: 'textarea',
          required: false,
          placeholder: 'Please describe any specific topics where you would like support or further information.'
        }
      ]
    }
  ]
};